// Theme definitions for domain cards
// Each theme contains styling information for different domain card visualizations

// Define new interfaces for structure and colors
export interface ThemeStructure {
  font: string;
  pattern: string;
  style: string;
  gradientDirection: string;
}

export interface ThemeColors {
  bg: string; // Color classes only, e.g., from-slate-900 via-purple-900 to-slate-900
  text: string;
  accent: string;
  border: string;
}

// Create separate arrays for structure and colors based on original themes

export const STRUCTURAL_CONFIGS: ThemeStructure[] = [
  // 1. Tech Startup
  {
    font: "font-['Inter']",
    pattern: 'circuit-board',
    style: 'font-semibold tracking-wide',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 2. E-commerce
  {
    font: "font-['Poppins']",
    pattern: 'product-grid',
    style: 'font-bold',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 3. Personal Brand
  {
    font: "font-['Playfair_Display']",
    pattern: 'minimal',
    style: 'font-medium tracking-tight',
    gradientDirection: 'bg-gradient-to-br',
  },
  // 4. Media
  {
    font: "font-['Poppins']",
    pattern: 'thumbnail',
    style: 'font-black uppercase tracking-wide',
    gradientDirection: 'bg-gradient-to-br',
  },
  // 5. Nature
  {
    font: "font-['Merriweather']",
    pattern: 'leafy',
    style: 'font-light tracking-wide',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 6. Education
  {
    font: "font-['Inter']",
    pattern: 'books',
    style: 'font-medium',
    gradientDirection: 'bg-gradient-to-br',
  },
  // 7. Trendy
  {
    font: "font-['Space_Grotesk']",
    pattern: 'y2k',
    style: 'font-black tracking-tight',
    gradientDirection: 'bg-gradient-to-bl',
  },
  // 8. Corporate
  {
    font: "font-['Inter']",
    pattern: 'grid',
    style: 'font-medium tracking-normal',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 9. Gaming
  {
    font: "font-['Oswald']",
    pattern: 'pixel',
    style: 'font-extrabold uppercase tracking-wider',
    gradientDirection: 'bg-gradient-to-br',
  },
  // 10. Experimental
  {
    font: "font-['Archivo_Black']",
    pattern: 'glitch',
    style: 'font-bold tracking-tight mix-blend-overlay',
    gradientDirection: 'bg-gradient-to-t', // Assign a default direction
  },
  // 11. Cyberpunk
  {
    font: "font-['Space_Grotesk']",
    pattern: 'matrix',
    style: 'font-bold uppercase tracking-widest',
    gradientDirection: 'bg-gradient-to-br',
  },
  // 12. Sunny
  {
    font: "font-['Poppins']",
    pattern: 'sunburst',
    style: 'font-semibold tracking-normal',
    gradientDirection: 'bg-gradient-to-br',
  },
  // 13. Forest
  {
    font: "font-['Merriweather']",
    pattern: 'woodgrain',
    style: 'font-normal tracking-wide',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 14. Beach
  {
    font: "font-['Lobster']",
    pattern: 'waves',
    style: 'font-normal tracking-tight',
    gradientDirection: 'bg-gradient-to-tr',
  },
  // 15. Artistic
  {
    font: "font-['Caveat']",
    pattern: 'watercolor',
    style: 'font-bold italic tracking-normal',
    gradientDirection: 'bg-gradient-to-bl',
  },
  // 16. Luxury
  {
    font: "font-['Playfair_Display']",
    pattern: 'marble',
    style: 'font-medium uppercase tracking-wider',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 17. Industrial
  {
    font: "font-['Oswald']",
    pattern: 'concrete',
    style: 'font-bold uppercase tracking-tight',
    gradientDirection: 'bg-gradient-to-b',
  },
  // 18. Space
  {
    font: "font-['Space_Grotesk']",
    pattern: 'stars',
    style: 'font-semibold tracking-wide',
    gradientDirection: 'bg-gradient-to-b',
  },
  // 19. Vintage
  {
    font: "font-['Lobster']",
    pattern: 'vintage-paper',
    style: 'font-normal tracking-normal',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 20. Playful
  {
    font: "font-['Archivo_Black']",
    pattern: 'confetti',
    style: 'font-black uppercase tracking-tight',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 21. Finance Dark
  {
    font: "font-['Inter']",
    pattern: 'stock-chart',
    style: 'font-semibold tracking-normal',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 22. Soft Pastel
  {
    font: "font-['Caveat']",
    pattern: 'clouds',
    style: 'font-medium tracking-wide',
    gradientDirection: 'bg-gradient-to-br',
  },
  // 23. Bold Intense
  {
    font: "font-['Archivo_Black']",
    pattern: 'flames',
    style: 'font-black uppercase tracking-tighter',
    gradientDirection: 'bg-gradient-to-tr',
  },
  // 24. Clean Aquatic
  {
    font: "font-['Poppins']",
    pattern: 'bubbles',
    style: 'font-light tracking-wide',
    gradientDirection: 'bg-gradient-to-bl',
  },
  // 25. Desert Earthy
  {
    font: "font-['Merriweather']",
    pattern: 'sand',
    style: 'font-serif tracking-normal',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 26. Floral Gentle
  {
    font: "font-['Caveat']",
    pattern: 'floral',
    style: 'font-normal tracking-tight',
    gradientDirection: 'bg-gradient-to-br',
  },
  // 27. Sci-Fi Minimal
  {
    font: "font-['Roboto_Mono']",
    pattern: 'hexagons',
    style: 'font-mono font-medium tracking-wider',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 28. Regal Ornate
  {
    font: "font-['Playfair_Display']",
    pattern: 'damask',
    style: 'font-serif font-bold uppercase tracking-widest',
    gradientDirection: 'bg-gradient-to-bl',
  },
  // 29. Urban Gritty
  {
    font: "font-['Oswald']",
    pattern: 'brick',
    style: 'font-bold uppercase tracking-tight',
    gradientDirection: 'bg-gradient-to-tr',
  },
  // 30. Global Map
  {
    font: "font-['Inter']",
    pattern: 'map',
    style: 'font-sans tracking-wide',
    gradientDirection: 'bg-gradient-to-bl',
  },
  // 31. Candy Sweet
  {
    font: "font-['Poppins']",
    pattern: 'stripes',
    style: 'font-bold tracking-tight rounded-lg',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 32. Dark Mode Subtle
  {
    font: "font-['Inter']",
    pattern: 'subtle-grid',
    style: 'font-light tracking-normal',
    gradientDirection: 'bg-gradient-to-b',
  },
  // 33. Cinematic Dramatic
  {
    font: "font-['Oswald']",
    pattern: 'film-grain',
    style: 'font-bold uppercase tracking-widest',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 34. Ice Cream Fun
  {
    font: "font-['Lobster']",
    pattern: 'sprinkles',
    style: 'font-bold tracking-tight',
    gradientDirection: 'bg-gradient-to-tr',
  },
  // 35. Retro Tech 80s
  {
    font: "font-['Roboto_Mono']",
    pattern: 'scanlines',
    style: 'font-mono uppercase tracking-wide',
    gradientDirection: 'bg-gradient-to-br',
  },
  // 36. Calligraphy Script
  {
    font: "font-['Caveat']",
    pattern: 'parchment',
    style: 'font-normal tracking-wide',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 37. Healthy Organic
  {
    font: "font-['Poppins']",
    pattern: 'subtle-leaves',
    style: 'font-light tracking-normal',
    gradientDirection: 'bg-gradient-to-bl',
  },
  // 38. Blueprint Technical
  {
    font: "font-['Roboto_Mono']",
    pattern: 'blueprint-grid',
    style: 'font-mono tracking-wider',
    gradientDirection: 'bg-gradient-to-b',
  },
  // 39. Jazz Moody
  {
    font: "font-['Playfair_Display']",
    pattern: 'smoky',
    style: 'font-serif italic tracking-tight',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 40. Energetic Sporty
  {
    font: "font-['Archivo_Black']",
    pattern: 'streaks',
    style: 'font-black uppercase italic tracking-tighter',
    gradientDirection: 'bg-gradient-to-tr',
  },
  // 41. Classical Statue
  {
    font: "font-['Merriweather']",
    pattern: 'columns',
    style: 'font-serif font-medium tracking-wide',
    gradientDirection: 'bg-gradient-to-b',
  },
  // 42. Adventure Exploration
  {
    font: "font-['Lobster']",
    pattern: 'compass',
    style: 'font-bold tracking-tight',
    gradientDirection: 'bg-gradient-to-br',
  },
  // 43. Noir Detective
  {
    font: "font-['Roboto_Mono']",
    pattern: 'rain',
    style: 'font-mono font-light tracking-wider',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 44. Bakery Treats
  {
    font: "font-['Caveat']",
    pattern: 'gingham',
    style: 'font-medium tracking-normal',
    gradientDirection: 'bg-gradient-to-bl',
  },
  // 45. Steampunk Mechanical
  {
    font: "font-['Merriweather']",
    pattern: 'gears',
    style: 'font-serif font-bold tracking-widest',
    gradientDirection: 'bg-gradient-to-tr',
  },
  // 46. Music Festival Vibrant
  {
    font: "font-['Archivo_Black']",
    pattern: 'soundwave',
    style: 'font-black uppercase tracking-tight',
    gradientDirection: 'bg-gradient-to-r',
  },
  // 47. Zen Minimalist
  {
    font: "font-['Inter']",
    pattern: 'zen-garden',
    style: 'font-light tracking-wide',
    gradientDirection: 'bg-gradient-to-b',
  },
  // 48. Skater Grunge
  {
    font: "font-['Roboto_Mono']",
    pattern: 'checkerboard',
    style: 'font-mono font-bold uppercase tracking-wider',
    gradientDirection: 'bg-gradient-to-br',
  },
  // 49. Crystal Gemstone
  {
    font: "font-['Space_Grotesk']",
    pattern: 'crystal-facets',
    style: 'font-medium tracking-widest',
    gradientDirection: 'bg-gradient-to-bl',
  },
  // 50. Handmade Craft
  {
    font: "font-['Caveat']",
    pattern: 'stitches',
    style: 'font-normal tracking-tight',
    gradientDirection: 'bg-gradient-to-r',
  },
];

export const COLOR_CONFIGS: ThemeColors[] = [
  // 1. Tech Startup
  {
    bg: 'from-slate-900 via-purple-900 to-slate-900',
    text: 'text-white',
    accent: 'bg-cyan-400 text-slate-900',
    border: 'border-purple-500',
  },
  // 2. E-commerce
  {
    bg: 'from-blue-600 to-indigo-700',
    text: 'text-white',
    accent: 'bg-amber-400 text-indigo-900',
    border: 'border-blue-400',
  },
  // 3. Personal Brand
  {
    bg: 'from-indigo-600 to-purple-700',
    text: 'text-white',
    accent: 'bg-amber-300 text-purple-900',
    border: 'border-indigo-400',
  },
  // 4. Media
  {
    bg: 'from-red-600 to-orange-400',
    text: 'text-white',
    accent: 'bg-white text-red-600',
    border: 'border-red-400',
  },
  // 5. Nature
  {
    bg: 'from-emerald-100 to-teal-200',
    text: 'text-emerald-800',
    accent: 'bg-emerald-600 text-white',
    border: 'border-emerald-200',
  },
  // 6. Education
  {
    bg: 'from-blue-100 to-indigo-200',
    text: 'text-indigo-900',
    accent: 'bg-amber-400 text-indigo-900',
    border: 'border-indigo-200',
  },
  // 7. Trendy
  {
    bg: 'from-pink-400 via-purple-400 to-indigo-500',
    text: 'text-white',
    accent: 'bg-yellow-300 text-purple-800',
    border: 'border-pink-300',
  },
  // 8. Corporate
  {
    bg: 'from-blue-900 to-slate-800',
    text: 'text-gray-100',
    accent: 'bg-blue-500 text-white',
    border: 'border-slate-700',
  },
  // 9. Gaming
  {
    bg: 'from-gray-800 to-purple-900',
    text: 'text-white',
    accent: 'bg-green-500 text-white',
    border: 'border-purple-600',
  },
  // 10. Experimental
  {
    bg: 'from-pink-500 via-red-500 to-yellow-500',
    text: 'text-white',
    accent: 'bg-blue-300 text-pink-900',
    border: 'border-transparent',
  },
  // 11. Cyberpunk
  {
    bg: 'from-gray-900 via-purple-800 to-cyan-600',
    text: 'text-cyan-300',
    accent: 'bg-pink-500 text-white',
    border: 'border-cyan-500',
  },
  // 12. Sunny
  {
    bg: 'from-yellow-200 via-orange-300 to-pink-300',
    text: 'text-orange-800',
    accent: 'bg-blue-500 text-white',
    border: 'border-orange-300',
  },
  // 13. Forest
  {
    bg: 'from-green-800 via-lime-900 to-green-900',
    text: 'text-lime-100',
    accent: 'bg-amber-600 text-white',
    border: 'border-lime-700',
  },
  // 14. Beach
  {
    bg: 'from-sky-200 via-cyan-300 to-blue-400',
    text: 'text-sky-900',
    accent: 'bg-orange-500 text-white',
    border: 'border-cyan-300',
  },
  // 15. Artistic
  {
    bg: 'from-rose-400 via-fuchsia-500 to-indigo-500',
    text: 'text-white',
    accent: 'bg-lime-300 text-fuchsia-900',
    border: 'border-rose-300',
  },
  // 16. Luxury
  {
    bg: 'from-gray-700 via-gray-800 to-gray-900',
    text: 'text-amber-300',
    accent: 'bg-amber-400 text-gray-900',
    border: 'border-gray-600',
  },
  // 17. Industrial
  {
    bg: 'from-slate-300 to-slate-500',
    text: 'text-slate-900',
    accent: 'bg-red-600 text-white',
    border: 'border-slate-400',
  },
  // 18. Space
  {
    bg: 'from-indigo-900 via-black to-blue-900',
    text: 'text-blue-200',
    accent: 'bg-orange-400 text-black',
    border: 'border-indigo-700',
  },
  // 19. Vintage
  {
    bg: 'from-amber-100 via-orange-200 to-red-200',
    text: 'text-red-900',
    accent: 'bg-teal-600 text-white',
    border: 'border-orange-300',
  },
  // 20. Playful
  {
    bg: 'from-teal-300 via-cyan-400 to-sky-500',
    text: 'text-white',
    accent: 'bg-yellow-400 text-black',
    border: 'border-cyan-400',
  },
  // 21. Finance Dark
  {
    bg: 'from-emerald-900 via-green-900 to-gray-800',
    text: 'text-green-200',
    accent: 'bg-amber-500 text-black',
    border: 'border-emerald-700',
  },
  // 22. Soft Pastel
  {
    bg: 'from-pink-100 via-purple-100 to-indigo-100',
    text: 'text-purple-700',
    accent: 'bg-green-300 text-purple-800',
    border: 'border-purple-200',
  },
  // 23. Bold Intense
  {
    bg: 'from-red-700 via-rose-800 to-pink-900',
    text: 'text-pink-100',
    accent: 'bg-yellow-400 text-black',
    border: 'border-rose-600',
  },
  // 24. Clean Aquatic
  {
    bg: 'from-blue-200 via-cyan-100 to-sky-200',
    text: 'text-blue-800',
    accent: 'bg-orange-400 text-white',
    border: 'border-cyan-200',
  },
  // 25. Desert Earthy
  {
    bg: 'from-amber-700 via-orange-800 to-red-900',
    text: 'text-amber-100',
    accent: 'bg-cyan-600 text-white',
    border: 'border-orange-700',
  },
  // 26. Floral Gentle
  {
    bg: 'from-rose-100 via-pink-200 to-purple-200',
    text: 'text-rose-800',
    accent: 'bg-teal-500 text-white',
    border: 'border-pink-200',
  },
  // 27. Sci-Fi Minimal
  {
    bg: 'from-slate-100 to-slate-300',
    text: 'text-blue-700',
    accent: 'bg-purple-600 text-white',
    border: 'border-slate-300',
  },
  // 28. Regal Ornate
  {
    bg: 'from-purple-700 via-indigo-800 to-blue-900',
    text: 'text-yellow-300',
    accent: 'bg-yellow-400 text-indigo-900',
    border: 'border-indigo-600',
  },
  // 29. Urban Gritty
  {
    bg: 'from-gray-500 via-slate-600 to-gray-700',
    text: 'text-orange-400',
    accent: 'bg-yellow-500 text-black',
    border: 'border-slate-500',
  },
  // 30. Global Map
  {
    bg: 'from-cyan-700 via-teal-800 to-green-900',
    text: 'text-yellow-200',
    accent: 'bg-red-500 text-white',
    border: 'border-teal-600',
  },
  // 31. Candy Sweet
  {
    bg: 'from-pink-300 via-rose-300 to-fuchsia-400',
    text: 'text-purple-700',
    accent: 'bg-lime-400 text-black',
    border: 'border-rose-300',
  },
  // 32. Dark Mode Subtle
  {
    bg: 'from-gray-800 to-black',
    text: 'text-gray-400',
    accent: 'bg-blue-600 text-white',
    border: 'border-gray-700',
  },
  // 33. Cinematic Dramatic
  {
    bg: 'from-red-900 via-black to-orange-900',
    text: 'text-orange-200',
    accent: 'bg-white text-black',
    border: 'border-red-800',
  },
  // 34. Ice Cream Fun
  {
    bg: 'from-cyan-100 via-pink-100 to-yellow-100',
    text: 'text-pink-600',
    accent: 'bg-purple-400 text-white',
    border: 'border-pink-200',
  },
  // 35. Retro Tech 80s
  {
    bg: 'from-teal-600 via-cyan-700 to-blue-800',
    text: 'text-pink-300',
    accent: 'bg-yellow-400 text-black',
    border: 'border-cyan-600',
  },
  // 36. Calligraphy Script
  {
    bg: 'from-amber-50 to-orange-100',
    text: 'text-amber-900',
    accent: 'bg-purple-700 text-white',
    border: 'border-orange-200',
  },
  // 37. Healthy Organic
  {
    bg: 'from-lime-200 via-green-300 to-emerald-400',
    text: 'text-green-900',
    accent: 'bg-orange-500 text-white',
    border: 'border-green-300',
  },
  // 38. Blueprint Technical
  {
    bg: 'from-blue-700 to-blue-900',
    text: 'text-cyan-200',
    accent: 'bg-white text-blue-800',
    border: 'border-blue-600',
  },
  // 39. Jazz Moody
  {
    bg: 'from-indigo-800 via-purple-900 to-black',
    text: 'text-amber-400',
    accent: 'bg-red-600 text-white',
    border: 'border-purple-700',
  },
  // 40. Energetic Sporty
  {
    bg: 'from-orange-500 via-red-500 to-yellow-500',
    text: 'text-white',
    accent: 'bg-black text-white',
    border: 'border-red-600',
  },
  // 41. Classical Statue
  {
    bg: 'from-gray-100 to-gray-300',
    text: 'text-gray-700',
    accent: 'bg-amber-600 text-white',
    border: 'border-gray-300',
  },
  // 42. Adventure Exploration
  {
    bg: 'from-orange-700 via-yellow-800 to-lime-900',
    text: 'text-yellow-100',
    accent: 'bg-cyan-700 text-white',
    border: 'border-yellow-700',
  },
  // 43. Noir Detective
  {
    bg: 'from-black via-slate-800 to-gray-600',
    text: 'text-gray-300',
    accent: 'bg-red-700 text-white',
    border: 'border-slate-600',
  },
  // 44. Bakery Treats
  {
    bg: 'from-pink-200 via-rose-100 to-amber-100',
    text: 'text-rose-700',
    accent: 'bg-teal-400 text-white',
    border: 'border-rose-200',
  },
  // 45. Steampunk Mechanical
  {
    bg: 'from-amber-800 via-orange-900 to-stone-800',
    text: 'text-amber-200',
    accent: 'bg-cyan-500 text-black',
    border: 'border-orange-800',
  },
  // 46. Music Festival Vibrant
  {
    bg: 'from-purple-500 via-pink-500 to-orange-500',
    text: 'text-white',
    accent: 'bg-lime-400 text-black',
    border: 'border-pink-400',
  },
  // 47. Zen Minimalist
  {
    bg: 'from-teal-50 to-green-100',
    text: 'text-teal-800',
    accent: 'bg-amber-400 text-teal-900',
    border: 'border-green-200',
  },
  // 48. Skater Grunge
  {
    bg: 'from-slate-600 via-gray-700 to-red-800',
    text: 'text-red-300',
    accent: 'bg-yellow-400 text-black',
    border: 'border-gray-600',
  },
  // 49. Crystal Gemstone
  {
    bg: 'from-indigo-400 via-purple-500 to-pink-500',
    text: 'text-indigo-100',
    accent: 'bg-cyan-300 text-purple-900',
    border: 'border-purple-400',
  },
  // 50. Handmade Craft
  {
    bg: 'from-orange-100 via-yellow-100 to-lime-100',
    text: 'text-orange-900',
    accent: 'bg-blue-500 text-white',
    border: 'border-yellow-200',
  },
];

// Define a combined type for the final theme object
export type CombinedTheme = ThemeStructure & ThemeColors;
